import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { useParams, Link } from "react-router-dom";
import { Eye, Rocket, ArrowLeft, PlayCircle, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, coverSrc, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { serviceGroupOf, devGroupOf } from "../lib/domains";
import { Price } from "../components/Price";
import { Button } from "@/components/ui/button";

export default function AdminPreview() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/products/${id}`).then(({ data }) => setProduct(data)).catch((e) => setError(apiError(e, "Produit introuvable")));
  useEffect(() => { if (user?.role === "admin") load(); }, [id, user]);

  if (loading || (!product && !error)) {
    return <div className="flex min-h-[60vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-ciel" /></div>;
  }
  if (user?.role !== "admin") {
    return <div className="mx-auto max-w-2xl px-6 py-24 text-center"><p className="text-slate-500">Accès réservé à l'administrateur.</p></div>;
  }
  if (error) {
    return <div className="mx-auto max-w-2xl px-6 py-24 text-center"><p className="text-slate-500">{error}</p></div>;
  }

  const isPublished = product.status === "publie" || (!product.status && product.active);
  const isService = product.category === "Prestations de service";
  const isDev = product.category === "Développement informatique";
  const quoteOnly = product.quote_only || (isDev && product.dev_quote_only);
  const serviceGroup = isService ? serviceGroupOf(product) : null;
  const devGroup = isDev ? devGroupOf(product) : null;

  const publish = async () => {
    setBusy(true);
    try {
      await api.patch(`/products/${id}`, { status: "publie" });
      toast.success("Produit publié — visible en boutique");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-preview-page">
      {/* BANNIÈRE MODE APERÇU */}
      <div className={`sticky top-16 z-40 border-b px-6 py-3 ${isPublished ? "border-emerald-200 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid="preview-banner">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
          <p className={`flex items-center gap-2 text-xs font-semibold ${isPublished ? "text-emerald-700" : "text-amber-700"}`}>
            <Eye size={14} />
            {isPublished
              ? "MODE APERÇU — Ce produit est publié et visible en boutique"
              : "MODE APERÇU — Ce produit n'est pas encore publié"}
          </p>
          <div className="flex gap-2">
            {!isPublished && (
              <Button size="sm" data-testid="preview-publish-btn" onClick={publish} disabled={busy}
                className="rounded-full bg-navy text-white hover:bg-navy-light">
                <Rocket size={13} className="mr-1.5" /> Publier maintenant
              </Button>
            )}
            {isPublished && (
              <Link to={`/produit/${product.product_id}`} data-testid="preview-public-link"
                className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">
                <ExternalLink size={12} /> Voir la fiche publique
              </Link>
            )}
            <Link to="/admin" className="flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-1.5 text-xs font-semibold text-slate-600 hover:border-ciel">
              <ArrowLeft size={12} /> Retour au tableau
            </Link>
          </div>
        </div>
      </div>

      {/* FICHE TYPE PUBLIQUE */}
      <div className="mx-auto max-w-7xl px-6 py-12">
        <div className="grid gap-10 lg:grid-cols-2">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <img src={coverSrc(product.cover)} alt={product.title} className="max-h-[420px] w-full object-cover" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-navy px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">{product.category}</span>
              <span className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold text-slate-600">
                {product.product_type === "streaming"
                  ? <><PlayCircle size={12} className="text-ciel" /> Vidéo en streaming illimité</>
                  : <><Download size={12} className="text-ciel" /> Fichier téléchargeable (48 h, 3 usages)</>}
              </span>
              {serviceGroup && <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${serviceGroup.badge}`}>{serviceGroup.label}</span>}
              {devGroup && <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${devGroup.badge}`}>{devGroup.label}</span>}
            </div>
            <h1 className="mt-4 font-display text-2xl font-bold leading-tight tracking-tight text-navy md:text-3xl">{product.title}</h1>
            {isService && (
              <p className="mt-2 text-sm italic text-slate-500">
                {product.service_expert
                  ? `Expertise dispensée par Dr. ${product.service_expert} — Cabinet CARIS Santé, Abidjan, Côte d'Ivoire`
                  : "Expertise dispensée par le Cabinet CARIS Santé — Abidjan, Côte d'Ivoire"}
              </p>
            )}
            {isDev && (
              <p className="mt-2 text-sm italic text-slate-500">
                Développé avec Intelligence Artificielle Générative — Cabinet CARIS Santé, Abidjan, Côte d'Ivoire
              </p>
            )}
            {product.description?.includes("<") ? (
              <div className="mt-6 leading-relaxed text-slate-600" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.description || "") }} />
            ) : (
              <p className="mt-6 leading-relaxed text-slate-600">{product.description}</p>
            )}
            {product.presentation_video_url && (
              <video controls playsInline className="mt-6 w-full rounded-xl border border-slate-200"
                src={product.presentation_video_url.startsWith("http") ? product.presentation_video_url : `${API}/files/${product.presentation_video_url}`} />
            )}
            <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              {quoteOnly ? (
                <p className="font-display text-xl font-bold text-amber-600">Nous contacter pour un devis</p>
              ) : product.is_free ? (
                <p className="font-display text-2xl font-bold text-emerald-600">Gratuit</p>
              ) : (
                <Price xof={product.price} className="font-display text-3xl font-bold text-navy" />
              )}
              <p className="mt-3 text-xs text-slate-400">Boutons d'achat désactivés en mode aperçu.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
